import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Autocomplete, Box, Button, Chip, Collapse, Link, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useAuth } from '../../contexts/AuthContext';
import {
  AttentionItem,
  feedbackApi,
  getLearningAttention,
  getLearningConfigs,
  getLearningStatus,
  KnowledgeTrace,
  learningAdminApi,
  learningApi,
  LearningConfigSummary,
  learningPath,
  LearningStatus,
  snapshotsApi,
  TargetNode,
} from '../../services/learningApi';

/** Stable API clients bound to the current user's token. */
export function useApis() {
  const { getIdToken } = useAuth();
  const tokenRef = useRef(getIdToken);
  tokenRef.current = getIdToken;
  return useMemo(() => {
    const token = () => tokenRef.current();
    return {
      learning: learningApi(token),
      feedback: feedbackApi(token),
      snapshots: snapshotsApi(token),
      admin: learningAdminApi(token),
    };
  }, []);
}

/** Whether the Learning feature flag is on for this account (null while loading). */
export function useLearningStatus(): LearningStatus | null {
  const { user, getIdToken } = useAuth();
  const tokenRef = useRef(getIdToken);
  tokenRef.current = getIdToken;
  const [status, setStatus] = useState<LearningStatus | null>(null);
  useEffect(() => {
    if (!user) return;
    let alive = true;
    getLearningStatus(() => tokenRef.current()).then((s) => alive && setStatus(s));
    return () => {
      alive = false;
    };
  }, [user]);
  return status;
}

export function runLink(type: string, id: string): string {
  return type === 'chain_execution' ? `/dashboard/chain-run/${id}` : `/dashboard/run/${id}`;
}

export const RunLink: React.FC<{ type: string; id: string; available?: boolean; label?: string }> = ({ type, id, available = true, label }) => {
  if (!available) {
    return (
      <Tooltip title="This run's history has been deleted (data retention). The learning is kept.">
        <Chip size="small" label="history purged" variant="outlined" />
      </Tooltip>
    );
  }
  return (
    <Link component={RouterLink} to={runLink(type, id)} target="_blank" rel="noopener" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {label || `${id.slice(0, 8)}…`}
      <OpenInNewIcon sx={{ fontSize: 14 }} />
    </Link>
  );
};

const CYCLE_COLORS: Record<string, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  queued: 'default',
  running: 'info',
  completed: 'success',
  failed: 'error',
  cancelled: 'warning',
  skipped: 'warning',
};

const CYCLE_HELP: Record<string, string> = {
  queued: 'Waiting to start (only a limited number of analyses run at once).',
  running: 'Analyzing runs now. You can leave this page.',
  completed: 'Finished. Open it to see the report and which runs were analyzed.',
  failed: 'Stopped with an error. Anything learned before the error was kept.',
  cancelled: 'Stopped by a user. Anything learned before that was kept.',
  skipped: 'Not run, usually because there were too few new runs.',
};

export const CycleStatusChip: React.FC<{ status: string | null | undefined }> = ({ status }) =>
  status ? (
    <Tooltip title={CYCLE_HELP[status] || ''}>
      <Chip size="small" label={status} color={CYCLE_COLORS[status] || 'default'} variant={status === 'running' ? 'filled' : 'outlined'} />
    </Tooltip>
  ) : (
    <>—</>
  );

const ENTRY_COLORS: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
  candidate: 'default',
  active: 'success',
  pinned: 'primary',
  rejected: 'error',
  stale: 'warning',
};

export const ENTRY_STATUS_LABELS: Record<string, string> = {
  candidate: 'Waiting',
  active: 'In use',
  pinned: 'In use · pinned',
  rejected: 'Rejected',
  stale: 'Stale',
};

export const ENTRY_STATUS_HELP: Record<string, string> = {
  candidate:
    'Not used yet. It goes live on its own once enough runs back it up, or you can approve it now. A live fact comes back here if runs start contradicting it.',
  active: 'In use: published to the knowledge base and given to runs when relevant.',
  pinned: 'In use and locked in by you. It never goes stale and is never sent back to Waiting.',
  rejected: 'Not used. Kept so the analyzer does not learn it again.',
  stale: 'Not used any more: it has not shown up in runs for 3× the look-back period. It can go live again if it shows up again.',
};

export const EntryStatusChip: React.FC<{ status: string }> = ({ status }) => (
  <Tooltip title={ENTRY_STATUS_HELP[status] || ''}>
    <Chip
      size="small"
      label={ENTRY_STATUS_LABELS[status] || status}
      color={ENTRY_COLORS[status] || 'default'}
      variant={status === 'candidate' ? 'outlined' : 'filled'}
    />
  </Tooltip>
);

/** Small (i) icon with an explanation, for headers and labels. */
export const HelpTip: React.FC<{ title: React.ReactNode }> = ({ title }) => (
  <Tooltip title={title} arrow>
    <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', verticalAlign: 'middle', ml: 0.5, cursor: 'help' }} />
  </Tooltip>
);

/** Badge counts per target ("function:<id>" / "chain:<id>"), or an empty map when Learning is off. */
export function useLearningAttention(): Record<string, AttentionItem> {
  const { user, getIdToken } = useAuth();
  const status = useLearningStatus();
  const tokenRef = useRef(getIdToken);
  tokenRef.current = getIdToken;
  const [items, setItems] = useState<Record<string, AttentionItem>>({});
  useEffect(() => {
    if (!user || !status?.enabled) return;
    let alive = true;
    getLearningAttention(() => tokenRef.current()).then((list) => {
      if (!alive) return;
      const map: Record<string, AttentionItem> = {};
      list.forEach((i) => (map[`${i.target_type}:${i.target_id}`] = i));
      setItems(map);
    });
    return () => {
      alive = false;
    };
  }, [user, status?.enabled]);
  return items;
}

/** Self-improvement configs per target ("function:<original_uuid>" / "chain:<id>"), or an empty map when Learning is off. */
export function useLearningConfigs(status: LearningStatus | null): Record<string, LearningConfigSummary> {
  const { user, getIdToken } = useAuth();
  const tokenRef = useRef(getIdToken);
  tokenRef.current = getIdToken;
  const [items, setItems] = useState<Record<string, LearningConfigSummary>>({});
  useEffect(() => {
    if (!user || !status?.enabled) return;
    let alive = true;
    getLearningConfigs(() => tokenRef.current()).then((list) => {
      if (!alive) return;
      const map: Record<string, LearningConfigSummary> = {};
      list.forEach((c) => (map[`${c.target_type}:${c.target_id}`] = c));
      setItems(map);
    });
    return () => {
      alive = false;
    };
  }, [user, status?.enabled]);
  return items;
}

export function attentionLabel(item: AttentionItem): string {
  const parts = [];
  if (item.open_suggestions) parts.push(`${item.open_suggestions} suggestion${item.open_suggestions === 1 ? '' : 's'} to review`);
  if (item.waiting_entries) parts.push(`${item.waiting_entries} learning${item.waiting_entries === 1 ? '' : 's'} waiting`);
  return `Self-improvement: ${parts.join(', ')}`;
}

/** Small clickable count for lists (chains, functions); renders nothing when there's nothing to review. */
export const LearningAttentionBadge: React.FC<{ item: AttentionItem | undefined }> = ({ item }) => {
  const navigate = useNavigate();
  if (!item) return null;
  const total = item.open_suggestions + item.waiting_entries;
  if (!total) return null;
  const tab = item.open_suggestions ? 'suggestions' : 'knowledge';
  return (
    <Tooltip title={`${attentionLabel(item)}. Click to review.`}>
      <Chip
        size="small"
        color="secondary"
        icon={<AutoAwesomeIcon sx={{ fontSize: '0.8rem !important' }} />}
        label={total}
        onClick={(e) => {
          e.stopPropagation();
          navigate(learningPath(item.target_type, item.target_id, tab));
        }}
        sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0, '& .MuiChip-label': { px: 0.75 } }}
      />
    </Tooltip>
  );
};

/** Marks a chain/function that has had self-improvement suggestions applied. */
export const SelfImprovedChip: React.FC<{ item: AttentionItem | undefined }> = ({ item }) => {
  const navigate = useNavigate();
  const n = item?.applied_changes || 0;
  if (!item || !n) return null;
  const when = item.last_applied_at ? new Date(item.last_applied_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;
  return (
    <Tooltip
      title={
        <>
          {n} self-improvement change{n === 1 ? '' : 's'} applied.
          {item.last_applied_title && (
            <>
              <br />
              Latest: “{item.last_applied_title}”{when ? ` (${when})` : ''}. Applying a change saves it, which moves it up in lists sorted by last update.
            </>
          )}
          <br />
          Click to see what changed.
        </>
      }
    >
      <Chip
        size="small"
        variant="outlined"
        color="secondary"
        icon={<AutoAwesomeIcon sx={{ fontSize: '0.75rem !important' }} />}
        label={`Improved ×${n}`}
        onClick={(e) => {
          e.stopPropagation();
          navigate(learningPath(item.target_type, item.target_id));
        }}
        sx={{ height: 18, fontSize: '0.62rem', cursor: 'pointer', flexShrink: 0, '& .MuiChip-label': { px: 0.6 } }}
      />
    </Tooltip>
  );
};

/** "What knowledge was this run given" panel for run details pages. */
export const KnowledgeTraceView: React.FC<{ trace: KnowledgeTrace | null | undefined; title?: string }> = ({ trace, title = 'Knowledge used' }) => {
  const [open, setOpen] = useState(false);
  if (!trace) return null;
  const total = trace.learned + trace.category;
  const mapped = trace.calls > 1;
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mt: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
        <Typography variant="subtitle2">{title}</Typography>
        {total === 0 ? (
          <Chip size="small" variant="outlined" label="No matching knowledge found" />
        ) : (
          <>
            {trace.learned > 0 && (
              <Tooltip title="Snippets from self-improvement's auto-learned knowledge base">
                <Chip size="small" color="primary" variant="outlined" label={`${trace.learned} learned`} />
              </Tooltip>
            )}
            {trace.category > 0 && (
              <Tooltip title="Snippets from the knowledge categories configured for this step or call">
                <Chip size="small" variant="outlined" label={`${trace.category} from knowledge categories`} />
              </Tooltip>
            )}
          </>
        )}
        {mapped && (
          <Typography variant="caption" color="text.secondary">
            {trace.calls_with_knowledge} of {trace.calls} items received knowledge. Counts are totals; snippets are from the first item.
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {trace.snippets.length > 0 && (
          <Button size="small" onClick={() => setOpen(!open)}>
            {open ? 'Hide snippets' : 'Show snippets'}
          </Button>
        )}
      </Stack>
      <Collapse in={open}>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {trace.snippets.map((s, i) => (
            <Box key={i} sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover', borderLeft: 3, borderColor: s.source === 'learned' ? 'primary.main' : 'divider' }}>
              <Typography variant="caption" color="text.secondary">
                {s.source === 'learned' ? 'Learned' : 'Knowledge category'}
              </Typography>
              <Typography variant="body2" component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                {s.text}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Paper>
  );
};

export const StatCard: React.FC<{ label: string; value: React.ReactNode; hint?: React.ReactNode; action?: React.ReactNode }> = ({ label, value, hint, action }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="h5">{value}</Typography>
    {hint && (
      <Typography variant="body2" color="text.secondary">
        {hint}
      </Typography>
    )}
    {action && <Box sx={{ mt: 'auto', pt: 1 }}>{action}</Box>}
  </Paper>
);

export function credits(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(value < 10 ? 2 : 0)} credits`;
}

/** "Step 1 · normalize_input" for chain node ids like "node-1"; the id itself when unknown. */
export function stepLabel(nodes: TargetNode[], id: string | null | undefined): string {
  if (!id) return '';
  const n = nodes.find((x) => x.node_id === id);
  const num = /^node-(\d+)$/.exec(id)?.[1];
  const prefix = num ? `Step ${num}` : id;
  return n?.label ? `${prefix} · ${n.label}` : prefix;
}

/** Split pasted text into items: one per line (or per ";"), and per comma when `splitCommas`.
 * Bullets and numbering ("- ", "• ", "1. ") are removed. */
export function splitListText(text: string, splitCommas = false): string[] {
  const sep = /[\r\n]/.test(text) ? /[\r\n]+/ : text.includes(';') ? /;/ : splitCommas ? /,/ : null;
  const parts = sep ? text.split(sep) : [text];
  return parts.map((p) => p.replace(/^\s*(?:[-*•·]|\d+[.)])\s+/, '').trim()).filter(Boolean);
}

/** Free-text chip list. Type and press Enter, or paste a list; text left in the box is added on blur. */
export const ListField: React.FC<{
  label: string;
  value: string[];
  onChange: (v: string[]) => void;
  helperText?: string;
  splitCommas?: boolean;
  max?: number;
}> = ({ label, value, onChange, helperText, splitCommas = false, max }) => {
  const [input, setInput] = useState('');
  const [dropped, setDropped] = useState(0);
  const commit = (items: string[]) => {
    const seen = new Set(value.map((v) => v.toLowerCase()));
    const fresh = items.filter((x) => {
      const k = x.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    const room = max === undefined ? fresh.length : Math.max(0, max - value.length);
    setDropped(Math.max(0, fresh.length - room));
    if (room && fresh.length) onChange([...value, ...fresh.slice(0, room)]);
  };
  const hint = helperText || `Press Enter to add, or paste a list (one per line${splitCommas ? ' or comma-separated' : ''})`;
  const limit = max === undefined ? '' : ` · ${value.length}/${max}`;
  const overflow = dropped ? ` · ${dropped} not added (limit ${max})` : '';
  return (
    <Autocomplete
      multiple
      freeSolo
      size="small"
      options={[] as string[]}
      value={value}
      inputValue={input}
      onInputChange={(_, v, reason) => reason !== 'reset' && setInput(v)}
      onChange={(_, v) => {
        const typed = v.length > value.length ? String(v[v.length - 1]) : null;
        if (typed !== null) {
          setInput('');
          commit(splitListText(typed, splitCommas));
        } else {
          setDropped(0);
          onChange(v.map((x) => String(x).trim()).filter(Boolean));
        }
      }}
      renderTags={(v, getTagProps) => v.map((x, i) => <Chip size="small" label={x} {...getTagProps({ index: i })} />)}
      renderInput={(p) => (
        <TextField
          {...p}
          label={label}
          helperText={hint + limit + overflow}
          error={dropped > 0}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text');
            const items = splitListText(text, splitCommas);
            if (items.length > 1) {
              e.preventDefault();
              setInput('');
              commit(items);
            }
          }}
          onBlur={() => {
            if (input.trim()) {
              commit(splitListText(input, splitCommas));
              setInput('');
            }
          }}
        />
      )}
    />
  );
};
